// ============================================
// USER TYPES
// ============================================

export type UserRole = 'admin' | 'reseller' | 'client';
export type UserStatus = 'active' | 'inactive' | 'pending' | 'suspended';

export interface User {
  id: string;
  email: string;
  role: UserRole;
  status: UserStatus;
  firstName?: string;
  lastName?: string;
  phone?: string;
  companyName?: string;
  createdBy?: string;
  approvedBy?: string;
  approvedAt?: string;
  createdAt: string;
  updatedAt: string;
  lastLogin?: string;
  loginCount: number;
  metadata?: Record<string, any>;
}

export interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface AuthResponse {
  user: User;
  token: string;
  refreshToken: string;
  expiresAt: string;
}

// ============================================
// RESELLER TYPES
// ============================================

export interface ResellerProfile {
  id: string;
  userId: string;
  companyName?: string;
  companyAddress?: string;
  tradeLicense?: string;
  commissionRate: number;
  totalClients: number;
  activeClients: number;
  totalEarnings: number;
  pendingPayout: number;
  payoutMethod?: 'bkash' | 'nagad' | 'bank';
  payoutDetails?: Record<string, any>;
  notes?: string;
  createdAt: string;
}

export interface CommissionTransaction {
  id: string;
  resellerId: string;
  clientId: string;
  subscriptionId: string;
  subscriptionAmount: number;
  commissionRate: number;
  commissionAmount: number;
  status: 'pending' | 'approved' | 'paid' | 'cancelled';
  payoutReference?: string;
  paidAt?: string;
  createdAt: string;
  reseller?: { id: string; email: string; companyName?: string };
  client?: { id: string; email: string; companyName?: string };
}

// ============================================
// SUBSCRIPTION TYPES
// ============================================

export interface Feature {
  id: string;
  code: string;
  name: string;
  description?: string;
  category: string;
  icon: string;
  sortOrder: number;
  isActive: boolean;
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  slug: string;
  description?: string;
  priceBdt: number;
  priceUsd?: number;
  durationDays: number;
  maxRequestsPerDay: number;
  maxRequestsPerMonth: number;
  features: string[]; // Array of feature codes
  isActive: boolean;
  isFeatured: boolean;
  sortOrder: number;
}

export interface ClientSubscription {
  id: string;
  clientId: string;
  planId: string;
  resellerId?: string;
  status: 'active' | 'expired' | 'cancelled' | 'pending' | 'suspended';
  featureOverrides: {
    add: string[];
    remove: string[];
  };
  customDailyLimit?: number;
  customMonthlyLimit?: number;
  startsAt: string;
  expiresAt: string;
  cancelledAt?: string;
  paymentReference?: string;
  paymentMethod?: string;
  paymentStatus: 'pending' | 'paid' | 'failed';
  notes?: string;
  createdAt: string;
}

// ============================================
// ACTIVITY & USAGE TYPES
// ============================================

export interface ActivityLog {
  id: string;
  userId?: string;
  actorId?: string;
  action: string;
  resourceType?: string;
  resourceId?: string;
  oldValue?: Record<string, any>;
  newValue?: Record<string, any>;
  ipAddress?: string;
  userAgent?: string;
  createdAt: string;
}

export interface UsageLog {
  id: string;
  clientId: string;
  subscriptionId?: string;
  featureCode: string;
  requestTokens: number;
  responseTokens: number;
  processingTimeMs: number;
  success: boolean;
  errorMessage?: string;
  createdAt: string;
}

export interface DailyUsageSummary {
  clientId: string;
  date: string;
  totalRequests: number;
  successfulRequests: number;
  failedRequests: number;
  totalTokens: number;
  featureBreakdown: Record<string, number>;
}

// ============================================
// NOTIFICATION TYPES
// ============================================

export interface Notification {
  id: string;
  userId: string;
  type: string;
  title: string;
  message?: string;
  actionUrl?: string;
  isRead: boolean;
  readAt?: string;
  createdAt: string;
}

// ============================================
// DASHBOARD TYPES
// ============================================

export interface AdminDashboardStats {
  totalUsers: number;
  totalResellers: number;
  totalClients: number;
  activeSubscriptions: number;
  pendingApprovals: number;
  revenueThisMonth: number;
  revenueLastMonth: number;
  newClientsThisWeek: number;
  topResellers: Array<{
    id: string;
    email: string;
    companyName: string;
    activeClients: number;
    totalEarnings: number;
  }>;
  recentActivity: ActivityLog[];
}

export interface ResellerDashboardStats {
  totalClients: number;
  activeClients: number;
  pendingClients: number;
  totalEarnings: number;
  pendingPayout: number;
  thisMonthEarnings: number;
  recentClients: Array<{
    id: string;
    email: string;
    companyName: string;
    planName: string;
    status: string;
    createdAt: string;
  }>;
  recentCommissions: CommissionTransaction[];
}

export interface ClientDashboardStats {
  subscription: ClientSubscription | null;
  plan: SubscriptionPlan | null;
  availableFeatures: string[];
  usageToday: number;
  usageThisMonth: number;
  dailyLimit: number;
  monthlyLimit: number;
  daysRemaining: number;
  recentUsage: UsageLog[];
}

// ============================================
// FORM TYPES
// ============================================

export interface CreateUserForm {
  email: string;
  password: string;
  role: UserRole;
  firstName?: string;
  lastName?: string;
  phone?: string;
  companyName?: string;
  planId?: string;
  paymentReference?: string;
}

export interface CreateClientForm {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  companyName?: string;
  planId: string;
  paymentReference?: string;
}

export interface UpdateUserForm {
  firstName?: string;
  lastName?: string;
  phone?: string;
  companyName?: string;
  status?: UserStatus;
}

export interface CreatePlanForm {
  name: string;
  slug: string;
  description?: string;
  priceBdt: number;
  durationDays: number;
  maxRequestsPerDay: number;
  maxRequestsPerMonth: number;
  features: string[];
  isActive: boolean;
  isFeatured: boolean;
}

// ============================================
// API RESPONSE TYPES
// ============================================

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface PaginationParams {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
}

// ============================================
// GEO-PRO ENGINE TYPES (From existing app)
// ============================================

export enum OperationMode {
  ENTITY_INJECTION = 'ENTITY_INJECTION',
  SOCIAL_CAPTION = 'SOCIAL_CAPTION',
  F_COMMERCE_CONVERSION = 'FCOMMERCE_REPLY',
  TRUST_GAP_ANALYSIS = 'TRUST_GAP_AUDIT',
  EXPIRY_HELPER = 'EXPIRY_HELPER',
  COMPETITOR_ANALYSIS = 'COMPETITOR_ANALYSIS',
  JSON_LD_GENERATOR = 'JSONLD_GENERATOR',
  MAPS_GROUNDING = 'MAPS_GROUNDING',
  IMAGE_EDITING = 'AI_ART_PROMPT',
  INFOGRAPHIC_CONTENT = 'INFOGRAPHIC',
  SAAS_FRAMEWORK = 'SAAS_FRAMEWORK',
  MAP_PCP_CLIENTS = 'MAP_PCP_CLIENTS',
  MARKET_LINKAGE = 'MARKET_LINKAGE',
  VIDEO_GENERATION = 'VIDEO_GENERATION'
}

export interface ModeConfig {
  id: OperationMode;
  code: string;
  name: string;
  description: string;
  icon: string;
  placeholder: string;
  category: string;
}

export interface GeminiResponse {
  text: string;
  groundingLinks?: Array<{ title: string; uri: string }>;
  error?: string;
  tokensUsed?: number;
  processingTime?: number;
}
